import type { FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { baseApi } from "./baseApi";

interface ClientApiResponse<T = unknown> {
  success: boolean;
  message: string;
  data: T;
  errors: string[];
}

export interface ClientRecord {
  id: string;
  name: string;
  email: string;
  phone: string;
  city: string;
  role: string;
  isActive: boolean;
  createdAt?: string;
}

export interface CreateClientPayload {
  name: string;
  email: string;
  password: string;
  phone: string;
  city: string;
}

export interface UpdateClientPayload {
  name: string;
  phone: string;
  city: string;
  isActive: boolean;
}

export interface ClientPermissions {
  clientId: string;
  projectIds: string[];
}

const normalizeClient = (raw: Record<string, any>): ClientRecord => ({
  id: String(raw.id ?? ""),
  name: String(raw.name ?? ""),
  email: String(raw.email ?? ""),
  phone: String(raw.phone ?? ""),
  city: String(raw.city ?? ""),
  role: String(raw.role ?? "Client"),
  isActive: raw.isActive !== false,
  createdAt: raw.createdAt ?? undefined,
});

const unwrapData = (response: ClientApiResponse<unknown> | unknown) => {
  const data = (response as ClientApiResponse<unknown>)?.data ?? response;
  return data;
};

// Try each query in order; return on first success, last error on total failure.
const attemptQueryChain = async (
  baseQuery: (args: string | FetchArgs) => any,
  queries: FetchArgs[]
) => {
  let lastError: FetchBaseQueryError | undefined;
  for (const q of queries) {
    const result = await Promise.resolve(baseQuery(q));
    if (!result.error) return result;
    lastError = result.error;
  }
  return { error: lastError as FetchBaseQueryError };
};

// Handles string[], number[], and object[] (e.g. [{ projectId, id }]) responses.
const normalizePermissionIds = (raw: unknown): string[] => {
  const data = (raw as { data?: unknown })?.data ?? raw;
  if (!Array.isArray(data)) return [];
  return data
    .map((item) => {
      if (item && typeof item === "object") {
        const o = item as Record<string, any>;
        return String(o.projectId ?? o.projectID ?? o.id ?? "");
      }
      return String(item);
    })
    .filter(Boolean);
};

export const clientsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchClients: builder.query<ClientRecord[], void>({
      query: () => ({ url: "/Client", method: "GET" }),
      transformResponse: (response) => {
        const data = unwrapData(response);
        if (Array.isArray(data)) return data.map(normalizeClient);
        return [];
      },
      providesTags: ["Client"],
    }),

    createClient: builder.mutation<ClientApiResponse<ClientRecord>, CreateClientPayload>({
      query: (payload) => ({
        url: "/Client",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Client"],
    }),

    updateClient: builder.mutation<ClientApiResponse, { id: string; payload: UpdateClientPayload }>({
      query: ({ id, payload }) => ({
        url: `/Client/${id}`,
        method: "PUT",
        body: { id, ...payload },
      }),
      invalidatesTags: ["Client"],
    }),

    deleteClient: builder.mutation<ClientApiResponse, string>({
      query: (id) => ({
        url: `/Client/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Client"],
    }),

    fetchClientPermissions: builder.query<string[], string>({
      async queryFn(clientId, _api, _extraOptions, baseQuery) {
        const result = await attemptQueryChain(baseQuery, [
          { url: `/Client/${clientId}/permissions`,          method: "GET" },
          { url: `/ClientPermission?clientId=${clientId}`,   method: "GET" },
          { url: `/Permission/client/${clientId}`,           method: "GET" },
          { url: `/Project/client/${clientId}/permissions`,  method: "GET" },
        ]);
        if (result.error) return { error: result.error };
        return { data: normalizePermissionIds(result.data) };
      },
      providesTags: (_result, _error, clientId) => [
        { type: "ClientPermission" as const, id: clientId },
      ],
    }),

    updateClientPermissions: builder.mutation<ClientApiResponse, ClientPermissions>({
      async queryFn({ clientId, projectIds }, _api, _extraOptions, baseQuery) {
        const body = { projectIds, clientId };
        const result = await attemptQueryChain(baseQuery, [
          { url: `/Client/${clientId}/permissions`,         method: "PUT",  body: { projectIds } },
          { url: `/Client/${clientId}/permissions`,         method: "POST", body: { projectIds } },
          { url: `/ClientPermission`,                       method: "PUT",  body },
          { url: `/ClientPermission`,                       method: "POST", body },
          { url: `/Permission/client/${clientId}`,          method: "PUT",  body: { projectIds } },
          { url: `/Permission/client/${clientId}`,          method: "POST", body: { projectIds } },
        ]);
        if (result.error) return { error: result.error };
        return { data: result.data as ClientApiResponse };
      },
      invalidatesTags: (_result, _error, { clientId }) => [
        { type: "ClientPermission" as const, id: clientId },
      ],
    }),
  }),
});

export const {
  useFetchClientsQuery,
  useCreateClientMutation,
  useUpdateClientMutation,
  useDeleteClientMutation,
  useFetchClientPermissionsQuery,
  useUpdateClientPermissionsMutation,
} = clientsApi;
